// ROOM 404: Safety & Reporting Modal

import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, ShieldAlert, X } from 'lucide-react';
import { apiRequest } from '../services/api';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultTargetType?: 'message' | 'room' | 'profile' | 'event';
  defaultTargetId?: string;
}

export const ReportModal: React.FC<ReportModalProps> = ({
  isOpen,
  onClose,
  defaultTargetType = 'room',
  defaultTargetId = 'general',
}) => {
  const [targetType, setTargetType] = useState(defaultTargetType);
  const [targetId, setTargetId] = useState(defaultTargetId);
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError('Please select or specify a reason');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await apiRequest('/api/reports', {
        method: 'POST',
        body: {
          targetType,
          targetId: targetId.trim() || 'unspecified',
          reason: reason.trim(),
          details: details.trim() || undefined,
        },
      });

      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to submit report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
      <div className="relative w-full max-w-sm bg-[#12141c] border border-[#1f2433] rounded-2xl p-5 shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white p-1"
        >
          <X className="w-5 h-5" />
        </button>

        {submitted ? (
          <div className="py-6 text-center space-y-3">
            <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto" />
            <h3 className="text-base font-semibold text-white">Report Received</h3>
            <p className="text-xs text-gray-400">
              Our safety officers have logged your report. Your identity is never revealed to the reported party.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex items-center space-x-2 text-amber-400">
              <ShieldAlert className="w-5 h-5" />
              <h2 className="text-base font-bold text-white">Confidential Safety Report</h2>
            </div>

            <p className="text-xs text-gray-400">
              Perimeter policy: Community reports are confidential. Student real identities are protected and never disclosed to complainants.
            </p>

            {error && (
              <div className="p-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs">
                {error}
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Target Type</label>
              <select
                value={targetType}
                onChange={(e) => setTargetType(e.target.value as any)}
                className="w-full bg-[#090a0f] border border-[#1f2433] rounded-xl px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-emerald-500"
              >
                <option value="message">Message</option>
                <option value="room">Room</option>
                <option value="profile">Member Profile</option>
                <option value="event">Event</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Target ID / Alias</label>
              <input
                type="text"
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                placeholder="e.g. room name or member alias"
                className="w-full bg-[#090a0f] border border-[#1f2433] rounded-xl px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-emerald-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Reason</label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full bg-[#090a0f] border border-[#1f2433] rounded-xl px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-emerald-500"
              >
                <option value="">Select violation reason...</option>
                <option value="Harassment or bullying">Harassment or bullying</option>
                <option value="Doxxing or unmasking real identity">Doxxing or unmasking real identity</option>
                <option value="Hate speech or discrimination">Hate speech or discrimination</option>
                <option value="Safety or physical threat">Safety or physical threat</option>
                <option value="Spam or fraudulent impersonation">Spam or fraudulent impersonation</option>
                <option value="Explicit or non-consensual media">Explicit or non-consensual media</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Additional Context</label>
              <textarea
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="Describe what occurred (optional)..."
                rows={3}
                className="w-full bg-[#090a0f] border border-[#1f2433] rounded-xl px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-emerald-500 resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 px-4 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-xl text-sm transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
            >
              <AlertTriangle className="w-4 h-4" />
              <span>{loading ? 'Submitting Report...' : 'Submit Confidential Report'}</span>
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
